import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { Combobox, AdornedInput, Input, CALLBACK_FREQUENCY } from '@zillow/constellation';
import withLabel from './withLabel';

const MODES = {
    ADDRESS: 'allAddress',
    COUNTY: 'county',
    CITY: 'city',
    REGION: 'allRegion',
    ZIP: 'zipCode',
};

const createAutocompleteQuery = (mode, query, subdivision, code) => ({
    q: query,
    resultTypes: Array.isArray(mode) ? mode.join(',') : mode,
    viewState: subdivision,
    viewZip: code,
});

const AddressInputAutocomplete = ({
    mode,
    value,
    onSelection,
    onError,
    onChange,
    onBlur,
    needToPrefill,
    rightAdornment,
    queryAutocomplete,
    getRegionInformation,
    ...props
}) => {
    const [prefillComplete, setPrefillComplete] = useState(!needToPrefill);
    const [options, setOptions] = useState([]);
    const hasOptions = !!options.length;
    const queryRef = useRef(value);

    const handleSelection = useCallback(
        async option => {
            // If we are in address mode, we don't care about countyFIPS data
            if (mode === MODES.ADDRESS) {
                onSelection(option?.metaData);
            } else {
                const {
                    metaData: { state, lat, lng, city },
                    display,
                } = option;
                let {
                    metaData: { county, countyFIPS, zipCode },
                } = option;

                if (!zipCode) {
                    try {
                        const { location } = await getRegionInformation(lat, lng);
                        zipCode = location.zipCode;
                        county = county ?? location.countyName;
                    } catch {
                        // We are simply attempting to extend the object, if it can't find zip code, then perhaps
                        // the option was invalid
                    }
                }

                if (!county && zipCode) {
                    try {
                        const { results } = await queryAutocomplete(createAutocompleteQuery(MODES.ZIP, zipCode, state));
                        county = results[0].metaData.county;
                    } catch (error) {
                        // Same reasoning as above but for county
                        global.DD_RUM?.addError?.('Failed query autocomplete service for zipCode ', { error });
                    }
                }

                if (!countyFIPS && county) {
                    try {
                        const { results } = await queryAutocomplete(
                            createAutocompleteQuery(MODES.COUNTY, `${county}, ${state}`, state, zipCode)
                        );
                        countyFIPS = results[0].metaData.countyFIPS;
                    } catch (error) {
                        // Same reasoning as above
                        global.DD_RUM?.addError?.('Failed query autocomplete service for county ', { error });
                    }
                }

                const result = {
                    ...option.metaData,
                    display,
                    city,
                    county,
                    countyFIPS,
                    state,
                    zipCode,
                };
                onSelection(result);
            }
        },
        [getRegionInformation, queryAutocomplete, onSelection, mode]
    );

    const getSuggestions = useCallback(
        async (query, isPrefilling = false) => {
            // Make sure the query still exists for this request
            if (queryRef.current !== query) {
                return;
            }

            try {
                const inputs = createAutocompleteQuery(mode, query);
                const response = await queryAutocomplete(inputs);

                /*
                    Temporary solution to remove Canadian options, until
                    https://zbrt.atl.zillow.net/browse/SAT-594 is complete
                */
                const newOptions = (response?.results ?? [])
                    .map(({ display, ...rest }) => ({
                        ...rest,
                        display,
                        value: display,
                    }))
                    .filter(({ metaData }) => metaData?.country === 'United States' || metaData?.country === 'US');

                if (isPrefilling) {
                    const foundOption = newOptions.find(option => query === option.display);
                    foundOption ? handleSelection(foundOption) : onBlur();
                    // when the user enter the exact valid location
                } else if (newOptions.length === 1 && query === newOptions[0].display) {
                    handleSelection(response.results[0]);
                }
                setOptions(newOptions);
            } catch (e) {
                onError(e);
            }
        },
        [mode, onError, onBlur, handleSelection, queryAutocomplete]
    );

    useEffect(() => {
        if (!prefillComplete && value) {
            queryRef.current = value;
            getSuggestions(value, true);
            setPrefillComplete(true);
        }
    }, [prefillComplete, value, getSuggestions]);

    const debouncedGetSuggestions = useCallback(debounce(getSuggestions, 500), [getSuggestions]);

    const handleInputChange = useCallback(
        (query, meta) => {
            // ignore the selecting option action
            if (meta?.option?.display === query) {
                return;
            }
            onChange(query);
            queryRef.current = query.trim();
            if (queryRef.current) {
                debouncedGetSuggestions(queryRef.current);
            } else {
                setOptions([]);
            }
        },
        [debouncedGetSuggestions, onChange]
    );

    const renderInput = useCallback(
        ({ onClear, onTagClose, error, onBlur: comboboxOnBlur, autoFillLabel: autoComplete, ...rest }) => (
            <AdornedInput
                error={!!error}
                input={
                    <Input
                        {...rest}
                        value={value}
                        onBlur={(...args) => {
                            comboboxOnBlur(...args);
                            onBlur(...args);
                        }}
                        autoComplete={autoComplete}
                    />
                }
                rightAdornment={rightAdornment}
            />
        ),
        [onBlur, value, rightAdornment]
    );

    return (
        <Combobox
            freeForm
            selectCallbackFrequency={CALLBACK_FREQUENCY.ALWAYS}
            onOptionSelect={handleSelection}
            renderInput={renderInput}
            onChange={handleInputChange}
            getHighlightPattern={null}
            optionFilter={null}
            options={options}
            autoCompleteBehavior={hasOptions ? 'automatic' : 'none'}
            focusFirstOption={hasOptions}
            {...props}
        />
    );
};
AddressInputAutocomplete.propTypes = {
    mode: PropTypes.oneOfType([
        PropTypes.oneOf(Object.values(MODES)),
        PropTypes.arrayOf(PropTypes.oneOf(Object.values(MODES))),
    ]).isRequired,
    onSelection: PropTypes.func,
    onError: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.string,
    needToPrefill: PropTypes.bool,
    rightAdornment: PropTypes.node,
    queryAutocomplete: PropTypes.func.isRequired,
    getRegionInformation: PropTypes.func.isRequired,
};
AddressInputAutocomplete.defaultProps = {
    onError: () => {},
    onChange: () => {},
    onSelection: () => {},
    onBlur: () => {},
    value: '',
    needToPrefill: false,
};

const LabeledAddressInputAutocompleteField = withLabel(AddressInputAutocomplete);
LabeledAddressInputAutocompleteField.MODES = MODES;

export default LabeledAddressInputAutocompleteField;
